import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { useNotifications } from './useNotifications';
import { customerService } from '../lib/services/customerService';
import { customerHistoryService } from '../lib/services/customerHistoryService';
import { Customer, CreateCustomerData, CustomerChangeHistory } from '../types/customer';
import toast from 'react-hot-toast';
import { collection, query, orderBy, getDocs, getDoc, doc, where } from 'firebase/firestore';
import { subscribeShared } from '../lib/sharedQuery';
import { db } from '../lib/firebase';
import { useLanguage } from '../contexts/LanguageContext';

export const useCustomers = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerHistory, setCustomerHistory] = useState<Record<string, CustomerChangeHistory[]>>({});
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const { t } = useLanguage();
  const { createNotification } = useNotifications();

  // Kompatibilitás: a lista realtime frissül, kézi újratöltés nem szükséges
  const loadCustomers = useCallback(async () => { /* realtime – subscribeShared frissít */ }, []);

  useEffect(() => {
    if (!user) return;
    if (!hasAccess('customers')) { setLoading(false); return; }
    const isSuper = user.role === 'super_admin';
    const orgId = user.organizationId;
    if (!isSuper && !orgId) { setCustomers([]); setLoading(false); return; }
    const key = isSuper ? 'customers:all' : `customers:${orgId}`;
    const unsub = subscribeShared<Customer>(
      key,
      () => isSuper
        ? query(collection(db, 'customers'), orderBy('createdAt', 'desc'))
        : query(collection(db, 'customers'), where('organizations', 'array-contains', orgId)),
      (snap) => {
        const list = snap.docs.map(d => ({ ...d.data(), id: d.id, createdAt: d.data().createdAt?.toDate(), updatedAt: d.data().updatedAt?.toDate() })) as Customer[];
        list.sort((a, b) => ((a.createdAt instanceof Date ? a.createdAt.getTime() : 0) < (b.createdAt instanceof Date ? b.createdAt.getTime() : 0) ? 1 : -1));
        return list;
      },
      (data, err) => {
        if (err) { console.error('[useCustomers] Hiba az ügyfelek betöltése során:', err); setError(err as any); toast.error(t('common.messages.customer.failedToLoad')); setLoading(false); return; }
        setCustomers(data || []);
        setLoading(false);
        setError(null);
      }
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, user?.organizationId]);

  // Load history for a specific customer
  const loadCustomerHistory = useCallback(async (customerId: string) => {
    if (!customerId) return [];
    
    try {
      setHistoryLoading(true);
      const history = await customerHistoryService.getCustomerHistory(customerId);
      
      setCustomerHistory(prev => ({
        ...prev,
        [customerId]: history
      }));
      
      return history;
    } catch (err) {
      console.error('Error loading customer history:', err);
      // Re-throw the error so it can be caught by the component
      throw err;
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const createCustomer = async (data: CreateCustomerData) => {
    if (!user) return;
    try {
      const id = await customerService.create(data, user.uid);
      await loadCustomers();

      // Értesítés létrehozása a létrehozónak
      try {
        if (user.organizationId) {
          // Nyelv alapú fordítások betöltése
          const lang = t('common.language') === 'English' ? 'en' : 'hu';
          const translations = lang === 'en' 
            ? (await import('../translations/en')).default.notifications
            : (await import('../translations/hu')).default.notifications;
            
          const customerName: string = data.type === 'organization' 
            ? (data.companyName || 'Szervezet')
            : `${data.firstName} ${data.lastName}`;
            
          const titleTemplate = (translations.subjects?.customerCreated || (lang === 'hu' ? 'Új ügyfél létrehozva: {{customerName}}' : 'New customer created: {{customerName}}')) as string;
          const title = titleTemplate.replace('{{customerName}}', customerName);
            
          await createNotification({
            type: 'system',
            title: title,
            message: `${customerName} ${lang === 'hu' ? 'ügyfél sikeresen létrehozva' : 'customer successfully created'}.`,
            priority: 'low',
            userId: user.id,
            organizationId: user.organizationId,
            createdBy: user.id,
            createdByName: user.displayName,
            actionUrl: '/customers',
            data: { customerId: id }
          });
        }
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }

      return id;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  };

  const updateCustomer = async (id: string, data: Partial<Customer>) => {
    if (!user) return;
    try {
      await customerService.update(id, data, user.uid);
      await loadCustomers();
      
      // Refresh the history for this customer
      await loadCustomerHistory(id);

      // Értesítés létrehozása ha bármi változott
      try {
        if (user.organizationId) {
          // Nyelv alapú fordítások betöltése
          const lang = t('common.language') === 'English' ? 'en' : 'hu';
          const translations = lang === 'en' 
            ? (await import('../translations/en')).default.notifications
            : (await import('../translations/hu')).default.notifications;
          
          const customerName = data.companyName || (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : 'Ügyfél');
          
          // Státusz változás
          if (data.status) {
            const statusLabel = translations.statuses[data.status] || data.status;
              
            const titleTemplate = (translations.subjects?.customerStatusChanged || (lang === 'hu' ? 'Ügyfél státusz változott: {{customerName}}' : 'Customer status changed: {{customerName}}')) as string;
            const title = titleTemplate.replace('{{customerName}}', customerName);
              
            await createNotification({
              type: 'customer',
              title: title,
              message: `${customerName} ${translations.fields?.status || (lang === 'hu' ? 'státusza' : 'status')}: ${statusLabel}`,
              priority: 'low',
              userId: user.id,
              organizationId: user.organizationId,
              createdBy: user.id,
              createdByName: user.displayName,
              actionUrl: '/customers',
              data: { customerId: id }
            });
          } 
          // Egyéb módosítás (név, email, cím, stb.)
          else {
            const changedFields = [];
            if (data.companyName) changedFields.push(translations.fields?.companyName || (lang === 'hu' ? 'cégnév' : 'company name'));
            if (data.firstName || data.lastName) changedFields.push(translations.fields?.name || (lang === 'hu' ? 'név' : 'name'));
            if (data.email) changedFields.push(translations.fields?.email || 'email');
            if (data.phone) changedFields.push(translations.fields?.phone || (lang === 'hu' ? 'telefon' : 'phone'));
            if (data.address) changedFields.push(translations.fields?.address || (lang === 'hu' ? 'cím' : 'address'));
            if (data.taxNumber) changedFields.push(translations.fields?.taxNumber || (lang === 'hu' ? 'adószám' : 'tax number'));
            if (data.notes) changedFields.push(translations.fields?.notes || (lang === 'hu' ? 'megjegyzés' : 'notes'));
            
            if (changedFields.length > 0) {
              const titleTemplate = (translations.subjects?.customerDataUpdated || (lang === 'hu' ? 'Ügyfél adatai módosultak: {{customerName}}' : 'Customer data updated: {{customerName}}')) as string;
              const title = titleTemplate.replace('{{customerName}}', customerName);
                
              await createNotification({
                type: 'customer',
                title: title,
                message: `${customerName} ${lang === 'hu' ? 'adatai frissültek' : 'data updated'}: ${changedFields.join(', ')}`,
                priority: 'low',
                userId: user.id,
                organizationId: user.organizationId,
                createdBy: user.id,
                createdByName: user.displayName,
                actionUrl: '/customers',
                data: { customerId: id }
              });
            }
          }
        }
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  };

  const deleteCustomer = async (id: string) => {
    if (!user) return;
    try {
      await customerService.delete(id, user.uid);
      setCustomers(customers.filter(customer => customer.id !== id));
      
      // Remove history for deleted customer
      setCustomerHistory(prev => {
        const newHistory = { ...prev };
        delete newHistory[id];
        return newHistory;
      });
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  };

  const bulkDeleteCustomers = async (ids: string[]) => {
    if (!user) return { success: 0, failed: 0, errors: [] };
    try {
      const result = await customerService.bulkDelete(ids, user.uid);
      
      // Remove successfully deleted customers from state
      if (result.success > 0) {
        setCustomers(customers.filter(customer => !ids.includes(customer.id)));
        
        // Remove history for deleted customers
        setCustomerHistory(prev => {
          const newHistory = { ...prev };
          ids.forEach(id => delete newHistory[id]);
          return newHistory;
        });
      }
      
      return result;
    } catch (error) {
      console.error('Error bulk deleting customers:', error);
      throw error;
    }
  };

  const getCustomerById = useCallback(async (id: string) => {
    try {
      const customerDoc = await getDoc(doc(db, 'customers', id));
      if (customerDoc.exists()) {
        return { ...customerDoc.data(), id: customerDoc.id } as Customer;
      }
      return null;
    } catch (error) {
      console.error('Hiba az ügyfél lekérdezése során:', error);
      return null;
    }
  }, []);

  // Function to refresh a single customer by ID
  const refreshCustomer = useCallback(async (id: string) => {
    try {
      const refreshedCustomer = await getCustomerById(id);
      
      if (refreshedCustomer) {
        // Update the customers state by replacing the old customer with the fresh data
        setCustomers(currentCustomers => 
          currentCustomers.map(customer => 
            customer.id === id ? refreshedCustomer : customer
          )
        );
        return refreshedCustomer;
      }
      return null;
    } catch (error) {
      console.error('Error refreshing customer:', error);
      throw error;
    }
  }, [getCustomerById]);

  return {
    customers,
    loading,
    error,
    customerHistory,
    historyLoading,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    bulkDeleteCustomers,
    loadCustomerHistory,
    refresh: loadCustomers,
    getCustomerById,
    refreshCustomer
  };
}; 